import React, { useState, useRef, useEffect, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import Draggable from "react-draggable";
import { PMDialog } from "../../utils/PMDialog";
import { PMField } from "../../utils/PMField";
import { CameraIcon, CheckCircleIcon, XMarkIcon, FolderIcon, ArrowUpIcon } from "@heroicons/react/24/outline";
import { UploaderV4 } from "./Uploaderv4";
import { TWclassNames } from "../../utils/Div";
import PdfPlaceholder from "../../../utils/pdf-placeholder.png";
import { ProjectFolderSelector } from "../../utils/moduleSelectors/projectFolderSelector";
import { DrawingSelector } from "../../utils/moduleSelectors/drawingSelector";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import axios from "../../../api/axios";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const DocumentUploadSectionV6 = (props) => {
  const {
    multiple,
    module,
    moduleParam,
    parentCallback,
    onDocumentUploaded, // New callback for updating count
    required,
    disabled,
    iconOnly,
    buttonStyle = "default", // "default" or "compact"
    selectedFolder, // New prop for pre-selected folder
    onFolderChange, // New callback for folder changes
    project, // Project object - when provided, drawing selector will be shown
  } = props;
  const [open, setOpen] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedCount, setUploadedCount] = useState(0);
  const [startUpload, setStartUpload] = useState(null);
  const fileInputRef = useRef(null);
  const uploadFunctionRef = useRef(null);
  const [resetUploader, setResetUploader] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(selectedFolder || null);
  const [drawings, setDrawings] = useState({});
  const [positionPickerOpen, setPositionPickerOpen] = useState(false);
  const [positionPickerIndex, setPositionPickerIndex] = useState(null);
  const { t } = useTranslation();

  const allowedFileTypes =
    props.allowedFileTypes ||
    "image/png, image/jpeg, image/jpg, image/gif, image/svg, image/svg+xml, image/webp, application/pdf";

  // Sync folder state with prop changes
  useEffect(() => {
    setCurrentFolder(selectedFolder || null);
  }, [selectedFolder]);

  const handleFolderChange = (event) => {
    const folderId = event.target.value;
    const folder = folderId ? { id: folderId } : null;
    setCurrentFolder(folder);
    if (onFolderChange) {
      onFolderChange(folder);
    }
  };

  const handleFileSelection = (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (!selectedFiles.length) return;

    setFilesToUpload((prev) => [
      ...prev,
      ...selectedFiles.map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
        title: "",
        module,
        moduleParam,
        folder_id: currentFolder?.id || null, // Add folder information
        drawing_id: null,
        drawing_position_x: null,
        drawing_position_y: null,
        drawing_rotation: 0,
      })),
    ]);
    setOpen(true);
  };

  const handleTitleChange = (index, title) => {
    setFilesToUpload((prev) => {
      const updated = [...prev];
      updated[index].title = title;
      return updated;
    });
  };

  const handleDrawingChange = (index, drawing) => {
    setFilesToUpload((prev) => {
      const updated = [...prev];
      updated[index].drawing_id = drawing?.id || null;
      updated[index].drawing_position_x = null;
      updated[index].drawing_position_y = null;
      updated[index].drawing_rotation = 0;
      return updated;
    });
    setDrawings((prev) => ({ ...prev, [index]: drawing }));
  };

  const handlePositionChange = (index, position) => {
    setFilesToUpload((prev) => {
      const updated = [...prev];
      updated[index].drawing_position_x = position.x;
      updated[index].drawing_position_y = position.y;
      updated[index].drawing_rotation = position.rotation || 0;
      return updated;
    });
  };

  const handleOpenPositionPicker = (index) => {
    setPositionPickerIndex(index);
    setPositionPickerOpen(true);
  };

  const handlePositionConfirm = (position) => {
    if (positionPickerIndex !== null) {
      handlePositionChange(positionPickerIndex, position);
      setPositionPickerOpen(false);
      setPositionPickerIndex(null);
    }
  };

  const handleDeleteFile = (indexToDelete) => {
    setFilesToUpload((prev) => {
      const updated = prev.filter((_, index) => index !== indexToDelete);
      // If no files left, close the dialog
      if (updated.length === 0) {
        setOpen(false);
      }
      return updated;
    });
  };

  const handleUploadComplete = (uploadedFiles) => {
    setFilesToUpload([]);
    setIsUploading(false);
    if (parentCallback) {
      parentCallback(uploadedFiles);
    }
    // Call the new callback to update document count
    if (onDocumentUploaded) {
      onDocumentUploaded(uploadedFiles.length);
    }
    handleClose();
  };

  const handleProgressChange = (progress) => {
    setUploadProgress((prev) => {
      const newProgress = { ...prev, ...progress };
      const totalProgress = Object.values(newProgress).reduce(
        (acc, val) => acc + val,
        0
      );
      return newProgress;
    });
  };

  const handleAddMoreFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileUploadComplete = () => {
    setUploadedCount((prev) => prev + 1);
  };

  const handleClose = () => {
    setOpen(false);
    setFilesToUpload([]);
    setIsUploading(false);
    setUploadedCount(0);
    setUploadProgress({});
    uploadFunctionRef.current = null;
    setStartUpload(null);

    if (resetUploader) {
      resetUploader();
    }
  };

  // Compact button style for checklist items
  const renderCompactButton = () => (
    <button
      type="button"
      onClick={() => fileInputRef.current?.click()}
      disabled={disabled}
      className="p-1.5 text-gray-400 hover:text-purple-600 transition-colors duration-200 rounded hover:bg-purple-50 border border-blue-200 bg-blue-50"
      title="Upload Pictures"
    >
      <CameraIcon className="h-4 w-4" />
    </button>
  );

  // Default button style (original)
  const renderDefaultButton = () => (
    <div
      className={TWclassNames(
        required ? "border-2 border-red-400" : "",
        "text-center bg-blue-400 hover:bg-blue-600 border-gray-300 px-5 py-5"
      )}
    >
      <label
        htmlFor={"file-upload"}
        className={TWclassNames(
          disabled ? "cursor-not-allowed" : "bg-white",
          "relative cursor-pointer rounded-md font-medium text-white focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-400 focus-within:ring-offset-2"
        )}
      >
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="w-full h-full flex flex-row items-center justify-center"
        >
          <CameraIcon className="h-5 w-6 mr-2 text-white" />
          {!iconOnly && (
            <div className="flex-row">
              <span>Last opp dokument/ bilde</span>
            </div>
          )}
        </button>
      </label>
    </div>
  );

  return (
    <>
      {/* Hidden File Input */}
      <input
        accept={allowedFileTypes}
        type="file"
        multiple={multiple}
        ref={fileInputRef}
        className="sr-only"
        onChange={handleFileSelection}
      />

      {/* File Picker Button */}
      {buttonStyle === "compact"
        ? renderCompactButton()
        : renderDefaultButton()}

      {/* Upload Dialog */}
      <PMDialog
        open={open}
        onClose={() => setOpen(false)}
        title="Forhåndsvisning og opplasting"
      >
        {filesToUpload.length > 0 && (
          <>
            {/* Folder Selection */}
            {module === "project" && moduleParam && (
              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <FolderIcon className="h-5 w-5 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-800">Mappe:</span>
                </div>
                <ProjectFolderSelector
                  projectId={moduleParam}
                  selected={currentFolder}
                  onChange={handleFolderChange}
                />
                {currentFolder && (
                  <p className="text-xs text-blue-600 mt-1">
                    Dokumenter vil bli lastet opp til: {currentFolder.displayTitle || "Valgt mappe"}
                  </p>
                )}
              </div>
            )}

            {filesToUpload.map((file, index) => (
              <div
                key={index}
                className="flex flex-col gap-3 py-4 bg-gray-100 rounded-xl px-4 relative mb-3"
              >
                {/* Delete Button */}
                <button
                  type="button"
                  onClick={() => handleDeleteFile(index)}
                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 transition-colors duration-200 z-10"
                  title="Fjern bilde"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
                
                <img
                  src={
                    file.file.type === "application/pdf"
                      ? PdfPlaceholder
                      : file.previewUrl
                  }
                  className="h-40 object-cover rounded"
                  alt="Preview"
                />
                <PMField
                  name="title"
                  type="text"
                  label="Tittel"
                  value={file.title}
                  onChange={(e) => handleTitleChange(index, e.target.value)}
                />
                
                {/* Drawing Selector - Only show if project prop is provided */}
                {project && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Tegning (valgfritt)
                    </label>
                    <DrawingSelector
                      projectId={project?.id || (module === "project" ? moduleParam : null)}
                      selected={drawings[index] || (file.drawing_id ? { id: file.drawing_id } : null)}
                      onChange={(drawing) => handleDrawingChange(index, drawing)}
                      placeholder="Velg tegning..."
                      disabled={isUploading}
                    />
                    
                    {/* Position Picker */}
                    {drawings[index] && (
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Posisjon på tegning
                        </label>
                        {file.drawing_position_x !== null && file.drawing_position_y !== null ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">
                              X: {file.drawing_position_x.toFixed(2)}, Y: {file.drawing_position_y.toFixed(2)}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleOpenPositionPicker(index)}
                              disabled={isUploading}
                              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Endre posisjon
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleOpenPositionPicker(index)}
                            disabled={isUploading}
                            className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Velg posisjon på tegning
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Progress Bar */}
            {isUploading && filesToUpload.length > 0 && (
              <div className="mt-4 w-full bg-gray-300 rounded h-4">
                <div
                  className="h-full bg-blue-500 rounded"
                  style={{
                    width: `${
                      (Object.values(uploadProgress).reduce(
                        (acc, val) => acc + val,
                        0
                      ) /
                        (filesToUpload.length * 100)) *
                      100
                    }%`,
                  }}
                />
              </div>
            )}
            {/* Upload Status */}
            {isUploading && (
              <p className="text-center mt-2 text-sm text-gray-700">
                Opplasting {uploadedCount + 1} av {filesToUpload.length}
              </p>
            )}

            {/* Upload Complete Checkmark */}
            {uploadedCount === filesToUpload.length && isUploading && (
              <div className="flex justify-center items-center mt-4 text-green-500">
                <CheckCircleIcon className="h-6 w-6" />
                <span className="ml-2 text-green-600 font-semibold">
                  Opplasting fullført
                </span>
              </div>
            )}

            {/* Add More Files Button */}
            <button
              type="button"
              className="mt-4 flex items-center justify-center w-full bg-blue-500 text-white p-2 rounded"
              onClick={handleAddMoreFiles}
            >
              <CameraIcon className="h-5 w-5 mr-2" /> Legg til flere bilder
            </button>

            {/* Upload Button */}
            <div className="flex justify-between mt-4">
              <button
                className="w-20 bg-gray-600 text-white p-2 rounded"
                onClick={() => handleClose()}
              >
                Steng
              </button>

              <button
                disabled={isUploading || !uploadFunctionRef.current}
                className="w-20 p-2 rounded text-white bg-emerald-600 hover:bg-blue-500 disabled:bg-gray-400"
                onClick={() => {
                  if (!isUploading && typeof uploadFunctionRef.current === "function") {
                    setIsUploading(true);
                    setUploadedCount(0);
                    uploadFunctionRef.current().then(() => setIsUploading(false));
                  } else {
                    console.error(
                      "startUpload is not a function or is undefined:",
                      uploadFunctionRef.current
                    );
                  }
                }}
              >
                {isUploading ? "Laster opp..." : "Last opp"}
              </button>
            </div>
          </>
        )}
      </PMDialog>

      {/* Position Picker Dialog */}
      {positionPickerOpen && positionPickerIndex !== null && drawings[positionPickerIndex] && (
        <PMDialog
          open={positionPickerOpen}
          onClose={() => {
            setPositionPickerOpen(false);
            setPositionPickerIndex(null);
          }}
          title="Velg posisjon på tegning"
          size="4xl"
        >
          <DrawingPositionPicker
            drawing={drawings[positionPickerIndex]}
            position={
              filesToUpload[positionPickerIndex]?.drawing_position_x !== null &&
              filesToUpload[positionPickerIndex]?.drawing_position_y !== null
                ? {
                    x: filesToUpload[positionPickerIndex].drawing_position_x,
                    y: filesToUpload[positionPickerIndex].drawing_position_y,
                    rotation: filesToUpload[positionPickerIndex].drawing_rotation || 0,
                  }
                : null
            }
            onPositionChange={handlePositionConfirm}
            onClose={() => {
              setPositionPickerOpen(false);
              setPositionPickerIndex(null);
            }}
          />
        </PMDialog>
      )}

      {/* Upload Handler */}
      <UploaderV4
        filesToUpload={filesToUpload}
        onUploadStart={useCallback((uploadFn) => {
          if (uploadFn && typeof uploadFn === "function") {
            uploadFunctionRef.current = uploadFn;
            setStartUpload(true); // Set to true to indicate upload function is ready
          } else {
            console.error("Invalid upload function received:", uploadFn);
          }
        }, [])}
        onUploadComplete={handleUploadComplete}
        onProgressChange={handleProgressChange}
        onFileUploadComplete={handleFileUploadComplete}
        onReset={setResetUploader}
      />
    </>
  );
};

// Drawing Position Picker Component
const DrawingPositionPicker = ({ 
  drawing, 
  position, 
  onPositionChange, 
  onClose 
}) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const pdfContainerRef = useRef(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfScale, setPdfScale] = useState(1.0);
  const [pdfPosition, setPdfPosition] = useState({ x: 0, y: 0 });
  const [pdfBaseSize, setPdfBaseSize] = useState({ width: 0, height: 0 });
  const [pdfScaledSize, setPdfScaledSize] = useState({ width: 0, height: 0 });
  const [selectedPosition, setSelectedPosition] = useState(position || { x: null, y: null, rotation: 0 });
  const [markerPosition, setMarkerPosition] = useState(null);
  const [rotation, setRotation] = useState(position?.rotation || 0);
  const [isDraggingMarker, setIsDraggingMarker] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [panStartPos, setPanStartPos] = useState(null);

  useEffect(() => {
    // Debug: Log drawing object to see what we have
    console.log("DrawingPositionPicker - drawing object:", drawing);
    
    // Load PDF from drawing - check both document and document_id
    if (drawing?.document?.file?.url) {
      console.log("Loading PDF from drawing.document.file.url:", drawing.document.file.url);
      loadPdf(drawing.document.file.url);
    } else if (drawing?.document?.id) {
      console.log("Loading PDF from drawing.document.id:", drawing.document.id);
      loadPdfFromDocument(drawing.document.id);
    } else if (drawing?.document_id) {
      console.log("Loading PDF from drawing.document_id:", drawing.document_id);
      loadPdfFromDocument(drawing.document_id);
    } else {
      console.warn("DrawingPositionPicker - No document found in drawing object");
      // Try to fetch the full drawing object if we only have an ID
      if (drawing?.id) {
        console.log("Fetching full drawing object for ID:", drawing.id);
        loadDrawingById(drawing.id);
      }
    }
  }, [drawing]);

  useEffect(() => {
    if (pdfBaseSize.width > 0 && pdfBaseSize.height > 0) {
      const scaledWidth = pdfBaseSize.width * pdfScale;
      const scaledHeight = pdfBaseSize.height * pdfScale;
      setPdfScaledSize({ width: scaledWidth, height: scaledHeight });
    }
  }, [pdfBaseSize, pdfScale]);

  useEffect(() => {
    if (pdfScaledSize.width > 0 && pdfScaledSize.height > 0 && containerRef.current && pdfBaseSize.width > 0) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      const containerCenterX = containerWidth / 2;
      const containerCenterY = containerHeight / 2;
      
      // Set marker position to center of container
      setMarkerPosition({ x: containerCenterX, y: containerCenterY });
      
      // If we have an existing position, calculate PDF position to center that position
      if (selectedPosition.x !== null && selectedPosition.y !== null) {
        // Calculate where PDF center should be to put selectedPosition at container center
        const offsetX = (selectedPosition.x - (pdfBaseSize.width / 2)) * pdfScale;
        const offsetY = (selectedPosition.y - (pdfBaseSize.height / 2)) * pdfScale;
        
        const pdfCenterX = containerCenterX - offsetX;
        const pdfCenterY = containerCenterY - offsetY;
        
        const pdfX = pdfCenterX - (pdfScaledSize.width / 2);
        const pdfY = pdfCenterY - (pdfScaledSize.height / 2);
        
        setPdfPosition({ x: pdfX, y: pdfY });
      } else {
        // No existing position, center the PDF
        const centerX = (containerWidth - pdfScaledSize.width) / 2;
        const centerY = (containerHeight - pdfScaledSize.height) / 2;
        setPdfPosition({ x: centerX, y: centerY });
      }
    }
  }, [pdfScaledSize, pdfBaseSize, pdfScale, selectedPosition]);

  // Marker position is always at center of container, no need to update based on selectedPosition

  useEffect(() => {
    // Update selectedPosition and rotation when position prop changes
    if (position) {
      setSelectedPosition({ 
        x: position.x, 
        y: position.y, 
        rotation: position.rotation || 0 
      });
      setRotation(position.rotation || 0);
    } else {
      setSelectedPosition({ x: null, y: null, rotation: 0 });
      setRotation(0);
    }
  }, [position]);

  const loadPdf = async (url) => {
    try {
      const isAbsoluteUrl = url.startsWith('http://') || url.startsWith('https://');
      
      if (isAbsoluteUrl) {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to fetch PDF: ${res.status}`);
        const blob = await res.blob();
        setPdfUrl(URL.createObjectURL(blob));
      } else {
        const res = await axios({
          method: "get",
          url: url,
          responseType: "blob",
        });
        const blob = res.data;
        setPdfUrl(URL.createObjectURL(blob));
      }
    } catch (err) {
      console.error("Error loading PDF:", err);
      toast.error("Kunne ikke laste tegning");
    }
  };

  const loadPdfFromDocument = async (documentId) => {
    try {
      const response = await axios({
        method: "get",
        url: `/documents/${documentId}`,
        params: { fields: "id,title,file" },
      });
      
      if (response.data?.file?.url) {
        await loadPdf(response.data.file.url);
      } else {
        console.error("No file URL in document response:", response.data);
        toast.error("Kunne ikke finne fil-URL for tegningen");
      }
    } catch (err) {
      console.error("Error loading document:", err);
      toast.error("Kunne ikke laste tegning");
    }
  };

  const loadDrawingById = async (drawingId) => {
    try {
      const response = await axios({
        method: "get",
        url: `/drawings/${drawingId}`,
        params: { fields: "id,title,description,document.file" },
      });
      
      const fullDrawing = response.data;
      console.log("Loaded full drawing:", fullDrawing);
      
      // Try to load PDF from the full drawing object
      if (fullDrawing?.document?.file?.url) {
        await loadPdf(fullDrawing.document.file.url);
      } else if (fullDrawing?.document?.id) {
        await loadPdfFromDocument(fullDrawing.document.id);
      } else if (fullDrawing?.document_id) {
        await loadPdfFromDocument(fullDrawing.document_id);
      } else {
        toast.error("Tegningen har ingen dokument tilknyttet");
      }
    } catch (err) {
      console.error("Error loading drawing:", err);
      toast.error("Kunne ikke laste tegning");
    }
  };

  const handlePdfClick = (e) => {
    // Don't handle click if clicking on marker or rotation handle
    if (e.target.closest('.position-marker') || e.target.closest('.rotation-handle')) {
      return;
    }
    
    // Don't handle click if we just finished panning
    if (isPanning) {
      setIsPanning(false);
      return;
    }
    
    if (!containerRef.current || !pdfBaseSize.width || !pdfBaseSize.height) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const containerCenterX = containerRect.width / 2;
    const containerCenterY = containerRect.height / 2;
    
    // Calculate PDF position relative to container center
    const pdfCenterX = pdfPosition.x + (pdfScaledSize.width / 2);
    const pdfCenterY = pdfPosition.y + (pdfScaledSize.height / 2);
    
    // Calculate click position relative to PDF
    const clickX = e.clientX - containerRect.left;
    const clickY = e.clientY - containerRect.top;
    
    // Calculate offset from PDF center
    const offsetX = clickX - pdfCenterX;
    const offsetY = clickY - pdfCenterY;
    
    // Convert to PDF coordinates
    const x = (pdfBaseSize.width / 2) + (offsetX / pdfScale);
    const y = (pdfBaseSize.height / 2) + (offsetY / pdfScale);
    
    // Clamp to PDF bounds
    const clampedX = Math.max(0, Math.min(pdfBaseSize.width, x));
    const clampedY = Math.max(0, Math.min(pdfBaseSize.height, y));
    
    const newPosition = { x: clampedX, y: clampedY, rotation };
    setSelectedPosition(newPosition);
    // Marker position is always at center of container
    setMarkerPosition({ x: containerCenterX, y: containerCenterY });
  };

  const handleRotationChange = (e) => {
    const newRotation = parseFloat(e.target.value) || 0;
    setRotation(newRotation);
    if (selectedPosition.x !== null && selectedPosition.y !== null) {
      setSelectedPosition({ ...selectedPosition, rotation: newRotation });
    }
  };

  const handleMarkerDrag = (e, data) => {
    if (!containerRef.current) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const containerCenterX = containerRect.width / 2;
    const containerCenterY = containerRect.height / 2;
    
    // Calculate offset from container center
    const offsetX = data.x - containerCenterX;
    const offsetY = data.y - containerCenterY;
    
    // Calculate new PDF position to keep marker at center
    const newPdfX = pdfPosition.x - offsetX;
    const newPdfY = pdfPosition.y - offsetY;
    
    // Update PDF position (this effectively moves the PDF under the fixed marker)
    setPdfPosition({ x: newPdfX, y: newPdfY });
    
    // Calculate the PDF coordinates at the center point
    const pdfCenterX = newPdfX + (pdfScaledSize.width / 2);
    const pdfCenterY = newPdfY + (pdfScaledSize.height / 2);
    
    const offsetFromPdfCenterX = containerCenterX - pdfCenterX;
    const offsetFromPdfCenterY = containerCenterY - pdfCenterY;
    
    const x = (pdfBaseSize.width / 2) + (offsetFromPdfCenterX / pdfScale);
    const y = (pdfBaseSize.height / 2) + (offsetFromPdfCenterY / pdfScale);
    
    // Clamp to PDF bounds
    const clampedX = Math.max(0, Math.min(pdfBaseSize.width, x));
    const clampedY = Math.max(0, Math.min(pdfBaseSize.height, y));
    
    const newPosition = { x: clampedX, y: clampedY, rotation };
    setSelectedPosition(newPosition);
    
    // Keep marker at center
    setMarkerPosition({ x: containerCenterX, y: containerCenterY });
    
    // Reset panning flag after a delay
    setTimeout(() => setIsPanning(false), 100);
  };

  const handleConfirm = () => {
    if (selectedPosition.x !== null && selectedPosition.y !== null) {
      onPositionChange({ ...selectedPosition, rotation });
      onClose();
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600">
        Dra på tegningen for å panere. Klikk for å velge posisjon. Roter pilen for å indikere retning.
      </div>
      
      <div
        ref={containerRef}
        className="relative w-full h-96 overflow-hidden bg-gray-200 rounded-lg border border-gray-300"
        onClick={handlePdfClick}
        onMouseDown={(e) => {
          // Track if we're starting a pan operation
          if (e.target.closest('.pdf-draggable-handle') && !e.target.closest('.position-marker')) {
            setIsPanning(true);
          }
        }}
      >
        {pdfUrl && (
          <Draggable
            position={pdfPosition}
            onStart={() => {
              setIsPanning(true);
              setPanStartPos(pdfPosition);
            }}
            onStop={(e, data) => {
              setPdfPosition({ x: data.x, y: data.y });
              // Update selected position based on new PDF position
              if (containerRef.current) {
                const containerRect = containerRef.current.getBoundingClientRect();
                const containerCenterX = containerRect.width / 2;
                const containerCenterY = containerRect.height / 2;
                
                const pdfCenterX = data.x + (pdfScaledSize.width / 2);
                const pdfCenterY = data.y + (pdfScaledSize.height / 2);
                
                const offsetX = containerCenterX - pdfCenterX;
                const offsetY = containerCenterY - pdfCenterY;
                
                const x = (pdfBaseSize.width / 2) + (offsetX / pdfScale);
                const y = (pdfBaseSize.height / 2) + (offsetY / pdfScale);
                
                const clampedX = Math.max(0, Math.min(pdfBaseSize.width, x));
                const clampedY = Math.max(0, Math.min(pdfBaseSize.height, y));
                
                setSelectedPosition({ x: clampedX, y: clampedY, rotation });
              }
              // Delay resetting isPanning to prevent click event
              setTimeout(() => setIsPanning(false), 100);
            }}
            handle=".pdf-draggable-handle"
            cancel=".position-marker, .rotation-handle"
          >
            <div
              ref={pdfContainerRef}
              className="pdf-draggable-handle relative cursor-move"
              style={{
                zIndex: 1,
                width: pdfScaledSize.width > 0 ? `${pdfScaledSize.width}px` : 'auto',
                height: pdfScaledSize.height > 0 ? `${pdfScaledSize.height}px` : 'auto',
                display: 'inline-block',
                lineHeight: 0,
                margin: 0,
                padding: 0,
                boxSizing: 'border-box',
              }}
            >
              <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages }) => {}}
                onLoadError={(error) => {
                  console.error("PDF load error:", error);
                  toast.error("Kunne ikke laste PDF");
                }}
                loading={<div className="p-4">Laster tegning...</div>}
              >
                <div style={{ lineHeight: 0, display: 'inline-block' }}>
                  <Page 
                    pageNumber={1} 
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    scale={pdfScale}
                    className="react-pdf__Page"
                    onLoadSuccess={(page) => {
                      const viewport = page.getViewport({ scale: 1.0 });
                      setPdfBaseSize({ width: viewport.width, height: viewport.height });
                    }}
                  />
                </div>
              </Document>
              
            </div>
          </Draggable>
        )}
          
        {/* Position Marker with Arrow - Fixed to center of container */}
        {markerPosition && (
            <Draggable
              position={{ x: markerPosition.x, y: markerPosition.y }}
              onStop={handleMarkerDrag}
              onStart={() => {
                setIsDraggingMarker(true);
                setIsPanning(true);
              }}
              onDrag={(e, data) => {
                // Update PDF position during drag to keep marker visually at center
                if (containerRef.current) {
                  const containerRect = containerRef.current.getBoundingClientRect();
                  const containerCenterX = containerRect.width / 2;
                  const containerCenterY = containerRect.height / 2;
                  
                  const offsetX = data.x - containerCenterX;
                  const offsetY = data.y - containerCenterY;
                  
                  const newPdfX = pdfPosition.x - offsetX;
                  const newPdfY = pdfPosition.y - offsetY;
                  
                  setPdfPosition({ x: newPdfX, y: newPdfY });
                  
                  // Update selected position in real-time
                  const pdfCenterX = newPdfX + (pdfScaledSize.width / 2);
                  const pdfCenterY = newPdfY + (pdfScaledSize.height / 2);
                  
                  const offsetFromPdfCenterX = containerCenterX - pdfCenterX;
                  const offsetFromPdfCenterY = containerCenterY - pdfCenterY;
                  
                  const x = (pdfBaseSize.width / 2) + (offsetFromPdfCenterX / pdfScale);
                  const y = (pdfBaseSize.height / 2) + (offsetFromPdfCenterY / pdfScale);
                  
                  const clampedX = Math.max(0, Math.min(pdfBaseSize.width, x));
                  const clampedY = Math.max(0, Math.min(pdfBaseSize.height, y));
                  
                  setSelectedPosition({ x: clampedX, y: clampedY, rotation });
                }
              }}
              cancel=".rotation-handle"
            >
              <div
                className="position-marker"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  transform: 'translate(-50%, -50%)',
                  cursor: 'move',
                  zIndex: 1001,
                }}
              >
                    {/* Arrow pointing in rotation direction */}
                    <div
                      style={{
                        width: '0',
                        height: '0',
                        borderLeft: '8px solid transparent',
                        borderRight: '8px solid transparent',
                        borderBottom: '30px solid red',
                        transform: `rotate(${rotation}deg)`,
                        transformOrigin: 'center bottom',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                      }}
                    />
                    {/* Center dot */}
                    <div
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: 'red',
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      }}
                    />
                    {/* Rotation handle - dra dette for å rotere pilen */}
                    <div
                      className="rotation-handle"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const startX = e.clientX;
                        const startY = e.clientY;
                        const startRotation = rotation;
                        
                        // Calculate initial angle from center to mouse position
                        const rect = e.currentTarget.getBoundingClientRect();
                        const centerX = rect.left + rect.width / 2;
                        const centerY = rect.top + rect.height / 2;
                        const initialAngle = Math.atan2(startY - centerY, startX - centerX) * (180 / Math.PI);
                        
                        const handleMouseMove = (moveEvent) => {
                          const currentX = moveEvent.clientX;
                          const currentY = moveEvent.clientY;
                          
                          // Calculate angle from center to current mouse position
                          const currentAngle = Math.atan2(currentY - centerY, currentX - centerX) * (180 / Math.PI);
                          const deltaAngle = currentAngle - initialAngle;
                          
                          // Normalize rotation to 0-360
                          let newRotation = (startRotation + deltaAngle) % 360;
                          if (newRotation < 0) newRotation += 360;
                          
                          setRotation(newRotation);
                          if (selectedPosition.x !== null && selectedPosition.y !== null) {
                            setSelectedPosition({ ...selectedPosition, rotation: newRotation });
                          }
                        };
                        
                        const handleMouseUp = () => {
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                          setIsDraggingMarker(false);
                        };
                        
                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                      }}
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '-40px',
                        transform: `translate(-50%, 0) rotate(${rotation}deg)`,
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: 'blue',
                        border: '3px solid white',
                        cursor: 'grab',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1002,
                      }}
                      title="Dra for å rotere pilen"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.cursor = 'grab';
                        e.currentTarget.style.backgroundColor = '#2563eb';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.cursor = 'grab';
                        e.currentTarget.style.backgroundColor = 'blue';
                      }}
                    >
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: 'white',
                        }}
                      />
                    </div>
                  </div>
                </Draggable>
              )}
      </div>
      
      {selectedPosition.x !== null && selectedPosition.y !== null && (
        <div className="space-y-2">
          <div className="text-sm text-gray-600">
            Posisjon: X: {selectedPosition.x.toFixed(2)}, Y: {selectedPosition.y.toFixed(2)}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Rotasjon (grader):</label>
            <input
              type="number"
              min="0"
              max="360"
              value={rotation}
              onChange={handleRotationChange}
              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
            />
            <div className="text-sm text-gray-500">
              ({rotation.toFixed(0)}°)
            </div>
          </div>
        </div>
      )}
      
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          {t("Cancel")}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={selectedPosition.x === null || selectedPosition.y === null}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          Bekreft posisjon
        </button>
      </div>
    </div>
  );
};
